import "./ReportList.scss";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";
import { PO_TYPE } from "../PurchaseOrderStatus/ClientPurchaseOrder.data";
import { useEffect, useState } from "react";
import XetModal from "../../components/shared/xet-modal/XetModal";
import GenerateReportPopup from "../../components/GenerateReportPopup/GenerateReportPopup";
import { toast } from "react-toastify";
import { toastOptions } from "../../utils/toast";
import { commaFormat } from "../../utils/Helper";

import DueOverduePieChart from "../../components/DueOverduePieChart/DueOverduePieChart";
import PackagingSpendGraph from "../../components/PackagingSpendGraph/PackagingSpendGraph";
import OTIFChart from "../../components/OTIFChart/OTIFChart";
import ReportOverdueInvoice from "../../components/ReportOverdueInvoice/ReportOverdueInvoice";
import InvoiceStatusChart from "../../components/InvoiceStatusChart/InvoiceStatusChart";
import PaymentReceivedChart from "../../components/PaymentReceivedChart/PaymentReceivedChart";
import SkuConsumptionGraph from "../../components/SkuConsumptionGraph/SkuConsumptionGraph";
import { IInvoice, IInvoiceStatusChartData } from "../../models/Dasboard";

interface IDashboardDetail {
  active_purchase_orders: number;
  invoices_due: number;
  invoices_overdue: number;
  total_skus_managed: number;
  invoice_count: number;
  invoices_unpaid: number;
}

const ReportList = () => {
  const [showGenerateReport, setShowGenerateReport] = useState(false);
  const [totalSpentThisMonth, setTotalSpentThisMonth] = useState<null | number>(
    null
  );
  const [invoiceStatuses, setInvoiceStatuses] =
    useState<null | IInvoiceStatusChartData>(null);
  const [dashboardDetails, setDashboardDetail] =
    useState<IDashboardDetail | null>(null);
  const [overdueInvoices, setOverdueInvoices] = useState<IInvoice[]>([]);

  useEffect(() => {
    getDashboardDetails();
  }, []);

  const getDashboardDetails = async () => {
    const { data } = await ApiService().client.get(URLS.DASHBOARD.DETAILS);
    setDashboardDetail(data);
    setOverdueInvoices(data.overdue_invoices);
    console.log("DASHBOARD DETIALL ::", data);
    setInvoiceStatuses(data.invoice_statuses);
  };

  const exportToExcel = ({
    data,
    headers,
    filename,
    sheetName,
  }: {
    data: any[];
    headers: Record<string, string>;
    filename: string;
    sheetName: string;
  }) => {
    let exportableData: Record<string, any>[] = [];

    if (data.length) {
      // Normal export: map fields to header labels
      exportableData = data.map((item) => {
        const row: Record<string, any> = {};
        Object.entries(headers).forEach(([key, label]) => {
          row[label] = item[key] ?? "";
        });
        return row;
      });
    } else {
      // If no data, return only a single empty row with headers
      const headerRow: Record<string, string> = {};
      Object.values(headers).forEach((label) => {
        headerRow[label] = ""; // empty cell
      });
      exportableData = [headerRow];
    }

    const worksheet = XLSX.utils.json_to_sheet(exportableData);

    // 🔹 Calculate max width per column
    const columns = Object.keys(exportableData[0]);
    const colWidths = columns.map((col) => {
      const maxLength = Math.max(
        col.length,
        ...exportableData.map((row) => String(row[col] ?? "").length)
      );
      return { wch: maxLength + 2 }; // Add padding
    });

    worksheet["!cols"] = colWidths; // ✅ Set column widths

    // 🔹 Style header row
    const headerStyle = {
      font: { bold: true },
      fill: {
        patternType: "solid",
        fgColor: { rgb: "DCE6F1" },
      },
    };

    columns.forEach((col, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (!worksheet[cellAddress]) return;

      worksheet[cellAddress].s = headerStyle;
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, filename);
  };

  const handleInvoiceDownload = async () => {
    try {
      const url = `${URLS.PURCHASE_ORDER.DOWNLOAD_INVOICE}`;
      const { data } = await ApiService().client.get(
        `${url}?po_type=${PO_TYPE.PO_RECEIVED}`
      );

      const headers = {
        po_date: "PO Date",
        po_number: "PO Number",
        sku_name: "SKU Name",
        unit: "SKU Unit",
        ordered_qty: "Ordered Qty",
        delivered_qty: "Delivered Qty",
        pending_qty: "Pending Qty",
        location: "Location",
        city: "City",
        pincode: "Pincode",
      };

      exportToExcel({
        data: data?.data || [],
        headers,
        filename: "purchase_orders.xlsx",
        sheetName: "Purchase Orders",
      });

      toast.success("Downloaded successfully!", toastOptions);
    } catch (e) {
      console.error("Error Downloading Invoice", e);
    }
  };

  const handleGenerateReport = async (start: string, end: string) => {
    try {
      const url = `${URLS.PURCHASE_ORDER.DOWNLOAD_DELIVERY_SCHEDULE_INVOICE}`;
      const queryParams = [
        `po_type=${PO_TYPE.DELIVERED}`,
        `start_date=${start}`,
        `end_date=${end}`,
      ];
      const { data } = await ApiService().client.get(
        `${url}?${queryParams.join("&")}`
      );

      const headers = {
        po_date: "PO Date",
        po_number: "PO Number",
        sku_name: "SKU Name",
        sku_unit: "SKU Unit",
        ppu: "PPU",
        amount: "Amount",
        delivered_qty: "Delivered Qty",
        delivered_date: "Delivered Date",
        invoice: "Invoice",
        pod: "POD",
        location: "Location",
        city: "City",
        pincode: "Pincode",
      };

      // Preprocess data to add amount, fallback values
      const enrichedData = (data?.data || []).map((item: any) => ({
        ...item,
        sku_unit: item.unit || item.sku_unit,
        ppu: item.ppu || item.price || 0,
        amount: item.amount,
      }));

      exportToExcel({
        data: enrichedData,
        headers,
        filename: `delivery_schedules_${start}_to_${end}.xlsx`,
        sheetName: "Delivery Schedules",
      });

      toast.success("Downloaded successfully!", toastOptions);
      setShowGenerateReport(false);
    } catch (error) {
      console.error("Error Downloading Delivery Schedules Invoice", error);
    }
  };

  const handleTotalExpenditureChange = (expense: number) => {
    setTotalSpentThisMonth(expense);
  };

  return (
    <div className="report-list-main-page flex-column full-width">
      <div className="upper-bar">
        <p className="xetgo-font-tag bold px-16 py-12 flex-row gap-4 cursor-pointer">
          Reports
          <span className="slash">/</span>
          <span className="sub-title xetgo-font-tag">Dashboard</span>
        </p>
      </div>
      <div className="report-list-lower-main-container p-16">
        <div className="report-list-inner-container p-12 flex-column gap-6">
          <div className="py-6 flex-row gap-14">
            <div className="dashboard-info-container flex-row gap-12 px-14 py-10 flex-1">
              <img
                height={39}
                width={39}
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250707104620-Icon.svg"
                alt="dollar-icon"
              />
              <div className="flex-column gap-1">
                <p className="sub-title xetgo-font-tag bold">
                  Total Spend This Month
                </p>
                <p className="xetgo-font-subHeading  bolder">
                  ₹
                  {dashboardDetails &&
                    totalSpentThisMonth &&
                    commaFormat(totalSpentThisMonth.toString())}
                </p>
              </div>
            </div>
            <div className="dashboard-info-container flex-row gap-12 px-14 py-10 flex-1">
              <img
                height={39}
                width={39}
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250707105410-Icon.svg"
                alt="bars-icon"
              />
              <div className="flex-column gap-1">
                <p className="sub-title xetgo-font-tag bold">
                  Total SKUs Managed
                </p>
                <p className="xetgo-font-subHeading  bolder">
                  {dashboardDetails &&
                    commaFormat(dashboardDetails.total_skus_managed.toString())}
                </p>
              </div>
            </div>
            <div className="dashboard-info-container flex-row gap-12 px-14 py-10 flex-1">
              <img
                height={39}
                width={39}
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250707105518-Icon.svg"
                alt="dollar-icon"
              />
              <div className="flex-column gap-1">
                <p className="sub-title xetgo-font-tag bold">
                  Active Purchase Orders
                </p>
                <p className="xetgo-font-subHeading  bolder">
                  {dashboardDetails &&
                    commaFormat(
                      dashboardDetails.active_purchase_orders.toString()
                    )}
                </p>
              </div>
            </div>
            <div className="dashboard-info-container flex-row gap-12 px-14 py-10 flex-1">
              <img
                height={39}
                width={39}
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250707105749-Icon.svg"
                alt="dollar-icon"
              />
              <div className="flex-column gap-1">
                <p className="sub-title xetgo-font-tag bold">
                  Invoices Overdue
                </p>
                <p className="xetgo-font-subHeading  bolder">
                  {dashboardDetails &&
                    commaFormat(dashboardDetails.invoice_count.toString())}
                </p>
              </div>
            </div>
          </div>
          <div className="graphs-reports-main-container-wrapper flex-row gap-12">
            <div className="all-report-main-container flex-1 flex-column gap-12">
              <div className="flex-row gap-14">
                {dashboardDetails ? (
                  <DueOverduePieChart
                    invoicesDue={dashboardDetails.invoices_due}
                    invoicesOverdue={dashboardDetails.invoices_overdue}
                    invoicesUnpaid={dashboardDetails.invoices_unpaid}
                  />
                ) : (
                  <div className="empty-state-report-donut flex-row align-items-center justify-content-center">
                    <div className="mini-loader"></div>
                  </div>
                )}
                <PackagingSpendGraph
                  setTotalSpentThisMonth={handleTotalExpenditureChange}
                />
              </div>
              <div className="flex-row gap-14">
                <OTIFChart />
                <ReportOverdueInvoice overdueInvoices={overdueInvoices} />
              </div>

              <div className="flex-row gap-14">
                {invoiceStatuses ? (
                  <InvoiceStatusChart invoiceStatuses={invoiceStatuses} />
                ) : (
                  <div className="empty-state-report-donut flex-row align-items-center flex-1 justify-content-center">
                    <div className="mini-loader"></div>
                  </div>
                )}
                <PaymentReceivedChart />
              </div>
              <div className="flex-row gap-14">
                <SkuConsumptionGraph />
              </div>
            </div>
            <div className="reports-main-container p-12 flex-column gap-12 border-box">
              <p className="reports-title bold xetgo-font-button">Reports</p>
              <div className="all-reports-wrapper-container">
                <div className="all-reports-main-container flex-column gap-12">
                  <div
                    onClick={handleInvoiceDownload}
                    className="ongoing-purchase report-container flex-column gap-8 xetgo-font-tag px-16 py-27 cursor-pointer"
                  >
                    <p className="report-sub-title bold">
                      Ongoing Purchase Orders Report
                    </p>
                    <p className="report-sub-title">
                      Get a real-time view of all SKUs currently in active
                      procurement cycles.
                    </p>
                  </div>
                  <div
                    onClick={() => setShowGenerateReport(true)}
                    className="delivered-po report-container flex-column gap-8 xetgo-font-tag px-16 py-27 cursor-pointer"
                  >
                    <p className="report-sub-title bold">
                      {" "}
                      Delivered Purchase Orders Report
                    </p>
                    <p className="report-sub-title">
                      Get a real-time view of all SKUs currently in active
                      procurement cycles.
                    </p>
                  </div>
                </div>
                <div className="">
                  <XetModal
                    backdropClose={true}
                    open={showGenerateReport}
                    handleClose={() => setShowGenerateReport(false)}
                  >
                    <GenerateReportPopup
                      handleClose={() => setShowGenerateReport(false)}
                      handleGenerateReport={handleGenerateReport}
                    />
                  </XetModal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportList;
